import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Package } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface MentorProgram {
  id: string;
  title: string;
  description: string | null;
  session_count: number;
  total_tokens_required: number;
  discount_percentage: number;
  is_active: boolean;
}

interface MentorProgramManagerProps {
  mentorId: string;
}

export const MentorProgramManager = ({ mentorId }: MentorProgramManagerProps) => {
  const [programs, setPrograms] = useState<MentorProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<MentorProgram | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    session_count: '',
    total_tokens_required: '',
    discount_percentage: '0',
    is_active: true,
  });

  useEffect(() => {
    fetchPrograms();
  }, [mentorId]);

  const fetchPrograms = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_programs')
        .select('*')
        .eq('mentor_id', mentorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPrograms(data || []);
    } catch (error: any) {
      toast.error('Failed to load programs');
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const programData = {
      mentor_id: mentorId,
      title: formData.title,
      description: formData.description || null,
      session_count: parseInt(formData.session_count),
      total_tokens_required: parseInt(formData.total_tokens_required),
      discount_percentage: parseFloat(formData.discount_percentage),
      is_active: formData.is_active,
    };

    try {
      if (editingProgram) {
        const { error } = await supabase
          .from('mentor_programs')
          .update(programData)
          .eq('id', editingProgram.id);

        if (error) throw error;
        toast.success('Program updated successfully');
      } else {
        const { error } = await supabase
          .from('mentor_programs')
          .insert([programData]);

        if (error) throw error;
        toast.success('Program created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchPrograms();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save program');
      console.error('Error saving program:', error);
    }
  };

  const handleDelete = async (programId: string) => {
    if (!confirm('Are you sure you want to delete this program?')) return;

    try {
      const { error } = await supabase
        .from('mentor_programs')
        .delete()
        .eq('id', programId);

      if (error) throw error;
      toast.success('Program deleted successfully');
      fetchPrograms();
    } catch (error: any) {
      toast.error('Failed to delete program');
      console.error('Error deleting program:', error);
    }
  };

  const handleEdit = (program: MentorProgram) => {
    setEditingProgram(program);
    setFormData({
      title: program.title,
      description: program.description || '',
      session_count: program.session_count.toString(),
      total_tokens_required: program.total_tokens_required.toString(),
      discount_percentage: program.discount_percentage.toString(),
      is_active: program.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      session_count: '',
      total_tokens_required: '',
      discount_percentage: '0',
      is_active: true,
    });
    setEditingProgram(null);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const calculateTokensPerSession = (sessionCount: string, totalTokens: string) => {
    const sessions = parseInt(sessionCount);
    const tokens = parseInt(totalTokens);
    if (sessions > 0 && tokens > 0) {
      return (tokens / sessions).toFixed(0);
    }
    return '0';
  };

  if (loading) {
    return <div className="text-muted-foreground">Loading programs...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Session Programs</h3>
          <p className="text-sm text-muted-foreground">
            Create custom session bundles to upsell students
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Create Program
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingProgram ? 'Edit Program' : 'Create New Program'}
              </DialogTitle>
              <DialogDescription>
                Define a custom session bundle for your students
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Program Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Complete Starter Package"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what's included in this program..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="session_count">Sessions *</Label>
                  <Input
                    id="session_count"
                    type="number"
                    min="1"
                    value={formData.session_count}
                    onChange={(e) => setFormData({ ...formData, session_count: e.target.value })}
                    placeholder="5"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="total_tokens">Total Tokens *</Label>
                  <Input
                    id="total_tokens"
                    type="number"
                    min="1"
                    value={formData.total_tokens_required}
                    onChange={(e) => setFormData({ ...formData, total_tokens_required: e.target.value })}
                    placeholder="250"
                    required
                  />
                </div>
              </div>

              {formData.session_count && formData.total_tokens_required && (
                <p className="text-xs text-muted-foreground">
                  ≈ {calculateTokensPerSession(formData.session_count, formData.total_tokens_required)} tokens per session
                </p>
              )}

              <div>
                <Label htmlFor="discount">Discount % (optional)</Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                  placeholder="0"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active (visible to students)</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  {editingProgram ? 'Update Program' : 'Create Program'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDialogClose(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {programs.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No programs created yet</p>
            <p className="text-sm">Create your first session bundle to upsell students</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {programs.map((program) => (
            <Card key={program.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      {program.title}
                      {!program.is_active && (
                        <span className="text-xs font-normal text-muted-foreground">(Inactive)</span>
                      )}
                    </CardTitle>
                    {program.description && (
                      <CardDescription className="mt-2 whitespace-pre-line">{program.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(program)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(program.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Sessions:</span>{' '}
                    <span className="font-medium">{program.session_count}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Tokens:</span>{' '}
                    <span className="font-medium">{program.total_tokens_required}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Per Session:</span>{' '}
                    <span className="font-medium">
                      ≈{(program.total_tokens_required / program.session_count).toFixed(0)} tokens
                    </span>
                  </div>
                  {program.discount_percentage > 0 && (
                    <div>
                      <span className="text-muted-foreground">Discount:</span>{' '}
                      <span className="font-medium text-green-600">{program.discount_percentage}%</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
